import api from './api';

/**
 * Tax invoices. Every amount is a 2-dp string computed by the server — the UI
 * displays them but never adds them up itself.
 */

export const normalizeInvoice = (raw = {}) => ({
  id: raw.id,
  number: raw.invoice_number ?? null, // null for drafts
  financialYear: raw.financial_year ?? null,
  clientId: raw.billing_client_id,
  invoiceDate: raw.invoice_date ?? '',
  dueDate: raw.due_date ?? '',
  clientName: raw.client_name ?? '',
  clientGstin: raw.client_gstin ?? '',
  clientAddress: raw.client_address ?? '',
  clientCode: raw.client_code ?? '',
  clientStateCode: raw.client_state_code ?? '',
  taxType: raw.tax_type ?? null, // 'IGST' | 'CGST_SGST' | null
  taxLabel: raw.tax_label ?? '',
  gstRate: raw.gst_rate,
  taxableAmount: raw.taxable_amount,
  cgstAmount: raw.cgst_amount,
  sgstAmount: raw.sgst_amount,
  igstAmount: raw.igst_amount,
  taxAmount: raw.tax_amount,
  totalAmount: raw.total_amount,
  receivedAmount: raw.received_amount,
  balanceAmount: raw.balance_amount,
  amountInWords: raw.amount_in_words ?? '',
  status: raw.status,
  isOverdue: Boolean(raw.is_overdue),
  notes: raw.notes ?? '',
  issuedAt: raw.issued_at ?? null,
  cancelledAt: raw.cancelled_at ?? null,
  cancelReason: raw.cancel_reason ?? '',
  createdAt: raw.created_at ?? null,
  itemsCount: raw.items_count ?? raw.items?.length ?? 0,
  items: (raw.items ?? []).map((i) => ({
    id: i.id,
    projectId: i.billing_project_id ?? null,
    srNo: i.sr_no,
    description: i.description ?? '',
    designCode: i.design_code ?? '',
    hsnCode: i.hsn_code ?? '',
    price: i.price ?? '',
  })),
  // Only detail and payment responses carry payments/activity.
  payments: (raw.payments ?? []).map((p) => ({
    id: p.id,
    amount: p.amount,
    paymentDate: p.payment_date,
    mode: p.mode,
    reference: p.reference ?? '',
    notes: p.notes ?? '',
    createdAt: p.created_at ?? null,
  })),
  permissions: {
    canEdit: !!raw.permissions?.can_edit,
    canDelete: !!raw.permissions?.can_delete,
    canIssue: !!raw.permissions?.can_issue,
    canCancel: !!raw.permissions?.can_cancel,
    canRecordPayment: !!raw.permissions?.can_record_payment,
  },
  activity: (raw.activity ?? []).map((a) => ({
    action: a.action,
    description: a.description,
    user: a.user ?? null,
    properties: a.properties ?? null,
    createdAt: a.created_at,
  })),
  downloadFilename: raw.download_filename ?? null,
  client: raw.client
    ? {
        id: raw.client.id,
        name: raw.client.name,
        gstin: raw.client.gstin ?? '',
        clientCode: raw.client.client_code ?? '',
        stateCode: raw.client.state_code ?? '',
        stateName: raw.client.state_name ?? '',
        address: raw.client.address ?? '',
        isDeleted: Boolean(raw.client.deleted_at),
      }
    : null,
});

const normalizeTotals = (t) => ({
  taxType: t.tax_type,
  taxLabel: t.tax_label,
  gstRate: t.gst_rate,
  halfRate: t.half_rate,
  taxableAmount: t.taxable_amount,
  cgstAmount: t.cgst_amount,
  sgstAmount: t.sgst_amount,
  igstAmount: t.igst_amount,
  taxAmount: t.tax_amount,
  totalAmount: t.total_amount,
  receivedAmount: t.received_amount,
  balanceAmount: t.balance_amount,
  amountInWords: t.amount_in_words,
  stateMissing: Boolean(t.state_missing),
  clientStateCode: t.client_state_code ?? '',
  clientStateName: t.client_state_name ?? '',
});

const draftToApi = (d) => ({
  billing_client_id: d.clientId,
  invoice_date: d.invoiceDate,
  due_date: d.dueDate || null,
  gst_rate: d.gstRate === '' || d.gstRate == null ? null : d.gstRate,
  notes: d.notes || null,
  // Array order is the printed SR No.
  items: d.items.map((i) => ({
    billing_project_id: i.projectId || null,
    description: i.description,
    design_code: i.designCode || null,
    hsn_code: i.hsnCode || null,
    price: String(i.price ?? ''), // as typed; the server parses it
  })),
});

export const invoiceService = {
  getAll: async (params = {}) => {
    const { data } = await api.get('/billing/invoices', { params });
    const s = data.summary ?? {};
    return {
      items: data.data.data.map(normalizeInvoice),
      total: data.data.total,
      currentPage: data.data.current_page,
      lastPage: data.data.last_page,
      perPage: data.data.per_page,
      summary: {
        financialYear: s.financial_year,
        totalInvoiced: s.total_invoiced,
        received: s.received,
        outstanding: s.outstanding,
        invoiceCount: s.invoice_count,
        overdueCount: s.overdue_count,
        draftCount: s.draft_count,
      },
    };
  },
  getById: async (id) => normalizeInvoice((await api.get(`/billing/invoices/${id}`)).data.data),
  createDraft: async (d) => normalizeInvoice((await api.post('/billing/invoices', draftToApi(d))).data.data),
  updateDraft: async (id, d) => normalizeInvoice((await api.put(`/billing/invoices/${id}`, draftToApi(d))).data.data),
  delete: (id) => api.delete(`/billing/invoices/${id}`),
  issue: async (id) => normalizeInvoice((await api.post(`/billing/invoices/${id}/issue`)).data.data),
  cancel: async (id, reason) =>
    normalizeInvoice((await api.post(`/billing/invoices/${id}/cancel`, { reason })).data.data),
  duplicate: async (id) => normalizeInvoice((await api.post(`/billing/invoices/${id}/duplicate`)).data.data),

  /** Stateless totals with the server's own math. Pass `{ signal }` to cancel. */
  calculate: async ({ clientId, invoiceId, gstRate, items }, config = {}) =>
    normalizeTotals((await api.post('/billing/invoices/calculate', {
      billing_client_id: clientId,
      invoice_id: invoiceId || null,
      gst_rate: gstRate === '' || gstRate == null ? null : gstRate,
      items: items.map((i) => ({ price: i.price === '' ? null : i.price })),
    }, config)).data.data),

  recordPayment: async (id, p) =>
    normalizeInvoice((await api.post(`/billing/invoices/${id}/payments`, {
      amount: p.amount,
      payment_date: p.paymentDate,
      mode: p.mode,
      reference: p.reference || null,
      notes: p.notes || null,
    })).data.data),
  deletePayment: async (paymentId) => normalizeInvoice((await api.delete(`/billing/payments/${paymentId}`)).data.data),
};
